import { useCallback, useEffect, useState } from 'react'

import Tabs, { Tab } from '@src/quince-components/src/atoms/tabs'
import { getSelectedColorFromSelectedOptions } from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import CUSTOM_SECTION_CONTAINER_NAMES from '@src/quince-site-common/src/constants/customSectionContainerNames'
import CUSTOM_SECTION_CONTAINER_TYPES from '@src/quince-site-common/src/constants/customSectionContainerTypes'
import Image from '@src/quince-site-common/src/domains/Image'
import Product from '@src/quince-site-common/src/domains/Product'
import Bundle from '@src/quince-site-common/src/domains/Product/BundleProduct'
import { ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import RichText from '@src/quince-site-common/src/domains/RichText'
import productHelper, { getFormattedCustomFieldText } from '@src/quince-site-common/src/helpers/productHelper'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import useTrueFitWidgetVisibility from '@src/quince-site-common/src/organisms/true-fit/hooks/useTrueFitWidgetVisibility'
import { TrueFitWidget } from '@src/quince-site-common/src/organisms/true-fit/true-fit-widgets'
import pdpStateManager from '@src/quince-site-common/src/redux/globalStateManager/PDPStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import { getSanitizedHtml } from '@src/quince-site-common/src/utils/getSanitizedHtml'
import BundlePageSectionProps from '@src/quince-site-pdp/src/domains/BundlePageSectionProps'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'

import ProductDetailsC from '../../../atoms/productDetails'
import ProductPageSectionProps from '../../../domains/ProductPageSectionProps'

import { getDetailsAndCareConfig } from './productDetailsAndCare.helper'
import styles from './productDetailsAndCare.module.scss'

interface ProductDetailsIcons {
  displayTitle: string
  image?: Image
}

interface ProductDetailsTabWithReadMore {
  textContent: RichText | string | undefined | null
  productDetailsIcons?: ProductDetailsIcons[]
  tabId: string
  isActive: boolean
  colorId?: string
  productId?: number
  shouldRenderTruefitWidget?: boolean
}

interface TabConfig {
  id: string
  title: string
  content: RichText | string | undefined | null
  icons?: ProductDetailsIcons[]
  tracker: string
  shouldRenderTruefitWidget?: boolean
  productId?: number
  colorId?: string
}

const tabIds = {
  PRODUCT_DETAILS: 'productDetails',
  SIZEANDFIT: 'productSizeAndFit',
  CARE: 'productCare',
  BENEFITS: 'productBenefits',
  INGREDIENTS: 'productIngredients',
  NOTES: 'productNotes',
  INSTALLATION: 'productInstallation',
  SPECS: 'productSpecs',
  DIMENSIONS: 'productDimensions',
}

const ProductDetailsTabWithReadMore = ({
  textContent,
  productDetailsIcons,
  tabId,
  isActive,
  colorId,
  productId,
  shouldRenderTruefitWidget,
}: ProductDetailsTabWithReadMore) => {
  const [hasMoreText, sethasMoreText] = useState(false)
  const [readMore, setReadMore] = useState(false)
  const { deviceType } = useDeviceInfo()
  const isMobile = deviceType === DEVICE_TYPES.MOBILE
  const handleReadMore = () => {
    setReadMore(!readMore)
  }
  useEffect(() => {
    if (!hasMoreText) {
      const element = document.querySelector(`#${tabId}`)
      const height = element?.clientHeight
      if (height && height > 400) {
        sethasMoreText(true)
        setReadMore(true)
      }
    }
  }, [isMobile, isActive])

  return (
    <div id={tabId} className={styles.productDetailsAndCareTabs__tab}>
      {shouldRenderTruefitWidget ? (
        <div data-testid="truefit-widget">
          <TrueFitWidget colorId={colorId} productId={productId} parentContainerId="product_details" />
        </div>
      ) : null}
      <ProductDetailsC
        // eslint-disable-next-line react/no-danger
        textContent={<div dangerouslySetInnerHTML={{ __html: getSanitizedHtml(textContent) }} />}
        icons={productDetailsIcons}
        className={readMore ? styles.productDetailsAndCareTabs__showMoreAnimation : ''}
        shadowClassName={readMore ? styles.productDetailsAndCareTabs__innerBottomShadow : ''}
      />

      {hasMoreText && (
        <span onClick={handleReadMore} className={styles.productDetailsAndCareTabs__readMoreLink}>
          READ {readMore ? 'MORE' : 'LESS'}
        </span>
      )}
    </div>
  )
}

const ProductDetails = (props: ProductPageSectionProps | BundlePageSectionProps) => {
  const isBundle = 'bundle' in props
  const product = isBundle ? props.bundle : props.product
  const selectedOptions = useAppSelector<ProductVariantOption[]>(pdpStateManager, pdpStateManager.getSelectedOptions)
  const colorId = getSelectedColorFromSelectedOptions(selectedOptions)
  const productDetailsIcons = getProductDetailsIcons(product)
  const [activeTabId, setActiveTabId] = useState('productDetails')

  const productForTrueFitWidget = isBundle ? undefined : props.product
  const { showTruefitWidget } = useTrueFitWidgetVisibility(productForTrueFitWidget)
  const shouldRenderTruefitWidget = !isBundle && showTruefitWidget
  const selectedColorValue = selectedOptions.filter(o => o.name == ('Color' as ProductOptionName))[0]?.value

  const { contentContainerDisplayName, tabTitles } = getDetailsAndCareConfig(product)

  const getIsVisibleTrackParams = useCallback(() => {
    const productId = product.id
    const selectedColor = selectedOptions.filter(o => o.name == ('Color' as ProductOptionName))[0]?.value
    const variants = productHelper.getVariantsForOptions(product, selectedOptions)
    return {
      action: 'product_details_and_care',
      value: true,
      context: {
        productId,
        selectedColor,
        contentContainerType: CUSTOM_SECTION_CONTAINER_TYPES.PRODUCT_DETAILS,
        contentContainerName: CUSTOM_SECTION_CONTAINER_NAMES.PRODUCT_DESCRIPTION,
        trackingId: 'product-description',
        contentContainerDisplayName: contentContainerDisplayName,
        productProperties: getProductPropertiesForTracking(
          product as Product,
          variants[0]?.shopifyVariantId?.toString(),
        ),
      },
    }
  }, [product.id, selectedColorValue])

  const variants = productHelper.getVariantsForOptions(product, selectedOptions)

  const getTrackingMetaData = useCallback(
    () => ({
      context: {
        contentContainerType: CUSTOM_SECTION_CONTAINER_TYPES.PRODUCT_DETAILS,
        contentContainerName: CUSTOM_SECTION_CONTAINER_NAMES.PRODUCT_DESCRIPTION,
        trackingId: 'product-description',
        contentContainerDisplayName: contentContainerDisplayName,
        productProperties: getProductPropertiesForTracking(
          product as Product,
          variants[0]?.shopifyVariantId?.toString(),
        ),
      },
    }),
    [product, variants],
  )

  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(getIsVisibleTrackParams)

  const tabsConfig: TabConfig[] = [
    {
      id: tabIds.PRODUCT_DETAILS,
      title: tabTitles.productDetails,
      content: product.details,
      icons: productDetailsIcons,
      tracker: 'product-description_details',
    },
    ...(getFormattedCustomFieldText(product.sizeAndFit as unknown as string)
      ? [
          {
            id: tabIds.SIZEANDFIT,
            title: tabTitles.productSizeAndFit,
            content: product.sizeAndFit ?? '',
            tracker: 'product-description_size-and-fit',
            shouldRenderTruefitWidget: shouldRenderTruefitWidget || true,
            productId: product.id as number,
            colorId: colorId,
          },
        ]
      : []),
    ...(getFormattedCustomFieldText(product.careAndMaintenance as unknown as string)
      ? [
          {
            id: tabIds.CARE,
            title: tabTitles.productCare,
            content: product.careAndMaintenance ?? '',
            tracker: 'product-description_care',
          },
        ]
      : []),
    ...(getFormattedCustomFieldText((product as Product).benefits)
      ? [
          {
            id: tabIds.BENEFITS,
            title: tabTitles.productBenefits,
            content: (product as Product).benefits ?? '',
            tracker: 'product-description_benefits',
          },
        ]
      : []),
    ...(getFormattedCustomFieldText((product as Product).ingredients)
      ? [
          {
            id: tabIds.INGREDIENTS,
            title: tabTitles.productIngredients,
            content: (product as Product).ingredients ?? '',
            tracker: 'product-description_ingredients',
          },
        ]
      : []),
    ...(getFormattedCustomFieldText((product as Product).notes)
      ? [
          {
            id: tabIds.NOTES,
            title: tabTitles.productNotes,
            content: (product as Product).notes ?? '',
            tracker: 'product-description_notes',
          },
        ]
      : []),
    ...(getFormattedCustomFieldText((product as Product).installation)
      ? [
          {
            id: tabIds.INSTALLATION,
            title: tabTitles.productInstallation,
            content: (product as Product).installation ?? '',
            tracker: 'product-description_installation',
          },
        ]
      : []),
    ...(getFormattedCustomFieldText((product as Product).specs)
      ? [
          {
            id: tabIds.SPECS,
            title: tabTitles.productSpecs,
            content: (product as Product).specs ?? '',
            tracker: 'product-description_specs',
          },
        ]
      : []),
    ...(getFormattedCustomFieldText((product as Product).dimensions)
      ? [
          {
            id: tabIds.DIMENSIONS,
            title: tabTitles.productDimensions,
            content: (product as Product).dimensions ?? '',
            tracker: 'product-description_dimensions',
          },
        ]
      : []),
  ]

  return (
    <div ref={trackIsVisibleRef}>
      <Tabs
        activeTabId={activeTabId}
        onChange={activeId => {
          setActiveTabId(activeId)
        }}
        className={styles.productDetailsAndCareTabs}
        tracker={'product-description'}
      >
        {tabsConfig.map(tab => (
          <Tab
            key={tab.id}
            id={tab.id}
            title={tab.title}
            headerClassName={styles.headerClassName}
            panelClassName={styles.productDetailsAndCareTabs__details}
            tracker={tab.tracker}
            trackingMetaData={getTrackingMetaData()}
          >
            <ProductDetailsTabWithReadMore
              textContent={tab.content}
              productDetailsIcons={tab.icons}
              tabId={tab.id}
              isActive={activeTabId === tab.id}
              shouldRenderTruefitWidget={tab.shouldRenderTruefitWidget}
              productId={tab.productId}
              colorId={tab.colorId}
            />
          </Tab>
        ))}
      </Tabs>
    </div>
  )
}

const getProductDetailsIcons = (product: Product | Bundle): ProductDetailsIcons[] => {
  const icons = product.productDetailIcons?.map(ss => {
    return {
      displayTitle: ss.title || '',
      image: ss,
    }
  })
  return icons && icons.length > 0 ? icons : []
}

export default ProductDetails
